import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

(function () {
  const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
  const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

  const containers = Array.from(document.querySelectorAll('.starter-offer-selects'));
  if (!containers.length) return;

  /** @param {HTMLElement} container */
  const initializeStarterKit = (container) => {
    const purchaseMode = container.dataset.purchaseMode || 'subscription_only';
    const isMixedMode = purchaseMode === 'subscription_and_otp';
    const isOtpOnlyMode = purchaseMode === 'otp_only';
    const hasFrequency = container.dataset.hasFrequency === 'true';
    const localized = {
      helperReady: container.dataset.helperMsgReady || '',
      helperLocked: container.dataset.helperMsgLocked || '',
      addedToCart: container.dataset.addedText || Theme?.translations?.added || '',
      addFailed: container.dataset.errorAddFailed || '',
      genericError: container.dataset.errorGeneric || '',
    };
    const bundleProperty = container.dataset.bundleProperty || 'Starter Pack';
    const customPropertyKey = container.dataset.customPropertyKey || '';
    const customPropertyValue = container.dataset.customPropertyValue || '';
    const addToCartButtonEl = /** @type {HTMLButtonElement | null} */ (
      container.querySelector('[data-add-to-cart]')
    );
    const hasGiftOffer = Number(addToCartButtonEl?.dataset.productGift || 0) > 0;
    const defaultPurchaseType =
      container.dataset.defaultPurchaseType || (isOtpOnlyMode ? 'otp' : 'subscription');
    const flavourIds = Array.from(
      container.querySelectorAll('.starter-offer-select[data-select-id^="flavour"]')
    ).map((select) => /** @type {HTMLElement} */ (select).dataset.selectId || '');
    const state = /** @type {Record<string, any>} */ ({
      frequency: null,
      purchaseType: defaultPurchaseType,
    });
    flavourIds.forEach((selectId) => {
      state[selectId] = null;
    });
    const getSelectedFlavours = () => flavourIds.map((selectId) => state[selectId]);
    const allFlavoursSelected = () => flavourIds.every((selectId) => Boolean(state[selectId]));

    const setDefaultFrequency = () => {
      const defaultOption = /** @type {HTMLElement | null} */ (
        container.querySelector(
          '[data-select-id="frequency"] .starter-offer-option[data-default="true"]'
        )
      );
      const fallbackOption = /** @type {HTMLElement | null} */ (
        container.querySelector('[data-select-id="frequency"] .starter-offer-option')
      );
      const targetOption = /** @type {HTMLElement | null} */ (defaultOption || fallbackOption);
      if (!targetOption) return;

      const triggerText = /** @type {HTMLElement | null} */ (
        container.querySelector('[data-select-id="frequency"] .starter-offer-trigger-text')
      );
      const labelEl = /** @type {HTMLElement | null} */ (
        targetOption.querySelector('.starter-offer-option-text')
      );
      const label = labelEl ? labelEl.textContent : '';

      state.frequency = {
        value: targetOption.dataset.frequency,
        planId: targetOption.dataset.planId,
        label,
      };
      if (triggerText && label) triggerText.textContent = label;
    };

    const closeAll = () => {
      container.querySelectorAll('.starter-offer-options').forEach((opt) => {
        opt.classList.remove('is-open');
        const trigger = opt.previousElementSibling;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    };

    const priceUpdateTarget = container.closest('.shopify-section') || document;

    const computeDynamicPrice = () => {
      const { purchaseType, frequency } = state;
      const flavours = getSelectedFlavours();
      if (!flavours.length || flavours.some((flavour) => !flavour?.priceData)) return null;

      const planId = frequency?.planId;
      const isSubscription = purchaseType !== 'otp';

      const itemPrice = (item) => {
        if (
          isSubscription &&
          planId &&
          item.priceData.sellingPlans &&
          item.priceData.sellingPlans[planId] != null
        ) {
          return item.priceData.sellingPlans[planId];
        }
        return item.priceData.otpPrice;
      };

      const itemCompareAtPrice = (item) => item.priceData.compareAtPrice || item.priceData.otpPrice;

      return {
        price: flavours.reduce((total, flavour) => total + itemPrice(flavour), 0),
        compareAtPrice: flavours.reduce((total, flavour) => total + itemCompareAtPrice(flavour), 0),
      };
    };

    const dispatchDynamicPriceUpdate = () => {
      priceUpdateTarget.dispatchEvent(
        new CustomEvent('starter-kit:price-update', {
          bubbles: true,
          detail: {
            ...computeDynamicPrice(),
            purchaseType: state.purchaseType,
          },
        })
      );
    };

    const updatePurchaseTypeAttribute = () => {
      if (priceUpdateTarget instanceof Element) {
        priceUpdateTarget.setAttribute('data-purchase-type', state.purchaseType);
      }
    };

    const syncGallerySlideVisibility = () => {
      if (!(priceUpdateTarget instanceof Element)) return;

      const gallery = priceUpdateTarget.querySelector('slideshow-component');
      if (!gallery || typeof gallery.select !== 'function') return;

      const currentSlide = gallery.querySelector('slideshow-slide[aria-hidden="false"]');
      if (!currentSlide) return;

      const hiddenClass =
        state.purchaseType === 'otp'
          ? 'media-purchase-visibility--subscription'
          : 'media-purchase-visibility--otp';

      if (!currentSlide.classList.contains(hiddenClass)) return;

      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const fallbackIndex = slides.findIndex((slide) => !slide.classList.contains(hiddenClass));

      if (fallbackIndex >= 0) {
        gallery.select(fallbackIndex, undefined, { animate: false });
      }
    };

    const updateUI = () => {
      const button = /** @type {HTMLButtonElement | null} */ (
        container.querySelector('[data-add-to-cart]')
      );
      const helper = /** @type {HTMLElement | null} */ (container.querySelector('[data-helper]'));
      const helperMsg = /** @type {HTMLElement | null} */ (
        container.querySelector('[data-helper-msg]')
      );
      const frequencyGroup = /** @type {HTMLElement | null} */ (
        container.querySelector('[data-frequency-group]')
      );
      const requiresFrequency = hasFrequency && state.purchaseType !== 'otp';
      syncProductOptions();
      const ready = Boolean(allFlavoursSelected() && (!requiresFrequency || state.frequency));
      const shouldHideGiftHelper = !hasGiftOffer || (isMixedMode && state.purchaseType === 'otp');

      if (frequencyGroup && isMixedMode) {
        frequencyGroup.classList.toggle('hidden', state.purchaseType === 'otp');
      }

      if (button) button.disabled = !ready;
      if (helper) {
        helper.hidden = shouldHideGiftHelper;
        helper.classList.toggle('is-ready', ready && !shouldHideGiftHelper);
      }
      if (helperMsg) {
        helperMsg.textContent = ready ? localized.helperReady : localized.helperLocked;
      }

      dispatchDynamicPriceUpdate();
      updatePurchaseTypeAttribute();
      syncGallerySlideVisibility();
    };

    const clearSelectedFlavour = (selectId) => {
      const select = container.querySelector(`[data-select-id="${selectId}"]`);
      const triggerText = select?.querySelector('.starter-offer-trigger-text');
      const thumb = select?.querySelector('.starter-offer-trigger > .starter-offer-thumb');
      const fallbackText = select instanceof HTMLElement ? select.dataset.placeholder || '' : '';

      state[selectId] = null;
      if (triggerText) triggerText.textContent = fallbackText;
      if (thumb) thumb.replaceChildren();
      select?.classList.add('starter-offer-select--thumb-empty');
    };

    const syncProductOptions = () => {
      if (!isMixedMode) return;

      flavourIds.forEach((selectId) => {
        const select = container.querySelector(`[data-select-id="${selectId}"]`);
        if (!select) return;

        const selected = state[selectId];
        const subscriptionSelected = state.purchaseType !== 'otp';
        select.querySelectorAll('.starter-offer-option').forEach((option) => {
          const isAvailable = !subscriptionSelected || Boolean(option.dataset.productPlans);
          option.hidden = !isAvailable;
        });

        if (subscriptionSelected && selected && !selected.plans.length) {
          clearSelectedFlavour(selectId);
        }
      });
    };

    const errorElement = /** @type {HTMLElement | null} */ (
      container.querySelector('[data-add-to-cart-error]')
    );
    const liveRegion = /** @type {HTMLElement | null} */ (
      container.querySelector('[data-live-region]')
    );
    let errorTimeout = 0;

    const refreshCartData = async () => {
      const cartUrl = Theme?.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    };

    const handleProductSelect = () => {
      clearError();
    };

    /** @param {Event & { detail?: { source?: string } }} event */
    const handleCartError = (event) => {
      if (event?.detail?.source === 'starter-kit') {
        clearError();
      }
    };

    document.addEventListener(StandardEvents.productSelect, handleProductSelect);
    document.addEventListener(StandardEvents.cartError, handleCartError);

    const clearError = () => {
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.classList.add('hidden');
      }
    };

    /** @param {string} message */
    const showError = (message) => {
      if (!errorElement) return;
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
      if (liveRegion) {
        liveRegion.textContent = message;
      }
      if (errorTimeout) clearTimeout(errorTimeout);
      errorTimeout = setTimeout(() => {
        clearError();
        if (liveRegion) liveRegion.textContent = '';
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    };

    /** @param {HTMLElement} option */
    const selectOption = (option) => {
      const select = /** @type {HTMLElement | null} */ (option.closest('.starter-offer-select'));
      if (!select) return;

      const selectId = /** @type {string | undefined} */ (select.dataset.selectId);
      const triggerText = /** @type {HTMLElement | null} */ (
        select.querySelector('.starter-offer-trigger-text')
      );
      const thumbEl = /** @type {HTMLElement | null} */ (
        select.querySelector('.starter-offer-trigger > .starter-offer-thumb')
      );

      if (selectId && flavourIds.includes(selectId)) {
        const handle = option.dataset.handle;
        const boxId = option.dataset.boxId;
        const name =
          /** @type {HTMLElement | null} */ (option.querySelector('.starter-offer-option-text'))
            ?.textContent || '';
        const img = option.querySelector('img');
        const image = img ? img.src : '';
        const plans = option.dataset.productPlans || '';
        let priceData = null;
        if (option.dataset.priceData) {
          try {
            priceData = JSON.parse(atob(option.dataset.priceData));
          } catch (error) {
            priceData = null;
          }
        }

        state[selectId] = {
          handle,
          boxId,
          name,
          image,
          plans: plans.split(',').filter(Boolean),
          priceData,
        };
        if (triggerText) triggerText.textContent = name;

        if (thumbEl && image) {
          thumbEl.innerHTML = `<img src="${image}" alt="${name}">`;
          select.classList.remove('starter-offer-select--thumb-empty');
        }
      } else if (selectId === 'frequency' && hasFrequency) {
        const value = option.dataset.frequency;
        const planId = option.dataset.planId;
        const label =
          /** @type {HTMLElement | null} */ (option.querySelector('.starter-offer-option-text'))
            ?.textContent || '';
        state.frequency = { value, planId, label };
        if (triggerText) triggerText.textContent = label;
      }

      closeAll();
      updateUI();
    };

    /** @param {string} selectId @param {string | undefined} handle */
    const getPreselectedOption = (selectId, handle) => {
      const normalizedHandle = handle?.trim();
      if (!normalizedHandle) return null;

      return /** @type {HTMLElement | null} */ (
        container.querySelector(
          `.starter-offer-select[data-select-id="${selectId}"] .starter-offer-option[data-handle="${normalizedHandle}"]`
        )
      );
    };

    const applyPreselectedProducts = () => {
      flavourIds.forEach((selectId, index) => {
        const option = getPreselectedOption(selectId, container.dataset[`preselectedProduct-${index + 1}`]);
        if (option) selectOption(option);
      });
    };

    /** @param {MouseEvent} e */
    container.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      const trigger = target?.closest('.starter-offer-trigger');
      const option = target?.closest('.starter-offer-option');
      const purchaseChoice = target?.closest('[data-purchase-choice]');

      if (purchaseChoice && isMixedMode) {
        const purchaseChoiceEl = /** @type {HTMLElement} */ (purchaseChoice);
        state.purchaseType = purchaseChoiceEl.dataset.purchaseChoice || 'subscription';
        container.querySelectorAll('[data-purchase-choice]').forEach((btn) => {
          const buttonEl = /** @type {HTMLElement} */ (btn);
          const isActive = buttonEl.dataset.purchaseChoice === state.purchaseType;
          buttonEl.classList.toggle('is-active', isActive);
          buttonEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        closeAll();
        updateUI();
        return;
      }

      if (trigger) {
        const select = /** @type {HTMLElement | null} */ (trigger.parentElement);
        const options = /** @type {HTMLElement | null} */ (
          select?.querySelector('.starter-offer-options')
        );
        const isOpen = options?.classList.contains('is-open');
        closeAll();
        if (!isOpen) {
          options?.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      if (option) {
        selectOption(/** @type {HTMLElement} */ (option));
      }
    });

    /** @param {MouseEvent} e */
    document.addEventListener('click', (e) => {
      const target = /** @type {Node | null} */ (e.target);
      if (target && !container.contains(target)) closeAll();
    });

    const addToCartButton = container.querySelector('[data-add-to-cart]');
    if (addToCartButton) {
      /** @param {MouseEvent} evt */
      addToCartButton.addEventListener('click', async function (evt) {
        if (!allFlavoursSelected() || (hasFrequency && !state.frequency)) return;

        const button = /** @type {HTMLButtonElement} */ (evt.currentTarget);
        const giftId = Number(button.dataset.productGift || 0);
        const giftQuantity = Math.max(1, Number(button.dataset.giftQuantity || 1));
        const existingGiftInCart = false;
        const shouldIncludeGift = !isMixedMode || state.purchaseType !== 'otp';
        const linkGift = Boolean(giftId && shouldIncludeGift && giftQuantity > 1);

        this.disabled = true;
        clearError();
        const selectedFrequency =
          hasFrequency && state.purchaseType !== 'otp' ? state.frequency : null;
        const items = getSelectedFlavours().map((item) => {
          const properties = /** @type {Record<string, any>} */ ({
            _starter_pack: true,
            _bundle_type: bundleProperty,
          });
          if (linkGift) {
            properties._linked_gift = String(giftId);
            properties._linked_gift_quantity = String(giftQuantity);
          }
          if (customPropertyKey && customPropertyValue) {
            properties[customPropertyKey] = customPropertyValue;
          }
          const payload = /** @type {any} */ ({
            id: item.boxId,
            quantity: 1,
            properties,
          });
          if (
            !isOtpOnlyMode &&
            selectedFrequency &&
            selectedFrequency.planId &&
            item.plans.includes(selectedFrequency.planId)
          ) {
            payload.selling_plan = selectedFrequency.planId;
          }
          return payload;
        });

        if (!existingGiftInCart && giftId && shouldIncludeGift) {
          items.push(
            linkGift
              ? { id: giftId, quantity: giftQuantity, properties: { _linked_gift_line: '1' } }
              : { id: giftId, quantity: 1 }
          );
        }

        const itemCount = items.reduce((total, item) => total + (item.quantity || 0), 0);
        const deferredEventPromise = CartLinesUpdateEvent.createPromise();

        container.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({
              merchandiseId: item.id.toString(),
              quantity: item.quantity,
            })),
            promise: deferredEventPromise.promise,
          })
        );

        try {
          const fetchCfg = fetchConfig('jsib', { body: JSON.stringify({ items }) });
          const response = await fetch(Theme?.routes?.cart_add_url || '/cart/add.js', {
            ...fetchCfg,
            headers: {
              ...fetchCfg.headers,
            },
          });

          const data = await response.json().catch(() => ({}));

          if (!response.ok || data?.status) {
            const message = data?.message || localized.addFailed;
            const cartErrorEvent = new CartErrorEvent({
              error: message,
              code: 'INVALID',
              detail: {
                description: data?.description,
                errors: data?.errors,
                source: 'starter-kit',
              },
            });
            container.dispatchEvent(cartErrorEvent);
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
              detail: {
                didError: true,
                items: data?.items || [],
                source: 'starter-kit',
                sourceId: container.dataset.blockId || 'starter-kit',
                itemCount,
              },
            });
            showError(message);
            return;
          }

          clearError();
          const addedText = localized.addedToCart;
          if (liveRegion) {
            liveRegion.textContent = addedText;
            window.setTimeout(() => {
              if (liveRegion) liveRegion.textContent = '';
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          const cart = await refreshCartData();
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart?.items || [],
              source: 'starter-kit',
              sourceId: container.dataset.blockId || 'starter-kit',
              itemCount,
              didError: false,
            },
          });
        } catch (error) {
          const currentError = /** @type {{ message?: string }} */ (error);
          console.error(error);
          container.dispatchEvent(
            new CartErrorEvent({
              error: currentError?.message || 'There was a problem adding items to the cart.',
              code: 'SERVICE_UNAVAILABLE',
              detail: {
                source: 'starter-kit',
              },
            })
          );
          deferredEventPromise.reject(error);
          showError(localized.genericError);
        } finally {
          this.disabled = false;
          cartPerformance.measureFromEvent('add:user-action', evt);
        }
      });
    }

    if (hasFrequency) setDefaultFrequency();
    updateUI();
    applyPreselectedProducts();
  };

  containers.forEach((container) => initializeStarterKit(/** @type {HTMLElement} */ (container)));
})();
